import React, { useCallback, useEffect, useRef, useState } from "react";
import { useGet, api } from "../../api";
import {
  IKindredOffersResponse,
  IKindredBrandReference,
  KindredPublishStatus,
} from "../../interfaces";
import {
  Button,
  Spinner,
  Form,
  Row,
  Col,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Link } from "react-router";
import Select from "react-select";
import {
  listSearchKey,
  settingsType,
  useListSearchMemory,
  useSettings,
} from "../../hooks";
import { useAuth } from "../../hooks/auth";
import { format } from "date-fns";
import { Copy, Check, Code, Plus } from "react-feather";
import { BrandMultiSelect } from "../../components/workbench";

const publishedStatusOptions = [
  { value: "", label: "All" },
  { value: "0", label: "Draft" },
  { value: "1", label: "Awaiting Approval" },
  { value: "2", label: "Approved" },
  { value: "3", label: "Deactivated" },
];

const domainModeOptions = [
  { value: "both", label: "Both" },
  { value: "offer_domains", label: "Offer Domains" },
  { value: "campaign_domains", label: "Campaign Domains" },
];

const statusBadge = (status: number) => {
  const map: Record<number, { bg: string; label: string }> = {
    [KindredPublishStatus.Draft]: { bg: "secondary", label: "Draft" },
    [KindredPublishStatus.AwaitingApproval]: {
      bg: "warning",
      label: "Awaiting",
    },
    [KindredPublishStatus.Approved]: { bg: "success", label: "Approved" },
    [KindredPublishStatus.Deactivated]: { bg: "danger", label: "Deactivated" },
  };
  const info = map[status] || { bg: "secondary", label: String(status) };
  return <Badge bg={info.bg}>{info.label}</Badge>;
};

const CopyButton: React.FC<{ text: string; title?: string }> = ({
  text,
  title,
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{copied ? "Copied!" : title || "Copy"}</Tooltip>}
    >
      <button
        type="button"
        className="btn btn-link btn-sm p-0 ms-1"
        style={{ lineHeight: 1, verticalAlign: "middle" }}
        onClick={handleCopy}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </OverlayTrigger>
  );
};

export const KindredOffers: React.FC = () => {
  const { hasPreviousSearch, initialPage, rememberSearch, forgetSearch } =
    useListSearchMemory(listSearchKey.KINDRED_OFFERS);
  const [page, setPage] = useState(initialPage);
  const [limit] = useState(100);

  // Filters (persisted)
  const [brandId, setBrandId] = useSettings(
    settingsType.KINDRED_OFFERS_BRAND_ID
  );
  const [topBrands, setTopBrands] = useSettings(
    settingsType.KINDRED_OFFERS_TOP_BRANDS
  );
  const [countryCode, setCountryCode] = useSettings(
    settingsType.KINDRED_OFFERS_COUNTRY
  );
  const [publishedStatus, setPublishedStatus] = useSettings(
    settingsType.KINDRED_OFFERS_PUBLISHED_STATUS
  );
  const [source, setSource] = useSettings(settingsType.KINDRED_OFFERS_SOURCE);
  const [domain, setDomain] = useSettings(settingsType.KINDRED_OFFERS_DOMAIN);
  const [domainMode, setDomainMode] = useSettings(
    settingsType.KINDRED_OFFERS_DOMAIN_MODE
  );
  const [validatedOnly, setValidatedOnly] = useSettings(
    settingsType.KINDRED_OFFERS_VALIDATED_ONLY
  );
  const [active, setActive] = useSettings(settingsType.KINDRED_OFFERS_ACTIVE);

  // Auth for direct API calls
  const { getToken } = useAuth();

  // Reference data
  const refCountries = useGet<string[]>("kindred/reference/countries", true);
  const refNetworks = useGet<string[]>("kindred/reference/networks", true);

  // Brand async search for typeahead
  const [selectedBrands, setSelectedBrands] = useState<
    readonly { value: string; label: string }[]
  >([]);

  // Resolve stored brandIds on mount
  useEffect(() => {
    if (brandId && selectedBrands.length === 0) {
      (async () => {
        try {
          const token = await getToken();
          const ids = brandId.split(",").filter(Boolean);
          const resolved = await Promise.all(
            ids.map(async (id: string) => {
              try {
                const brand = await api.get<IKindredBrandReference>(
                  `kindred/reference/brands/${encodeURIComponent(id)}`,
                  {
                    headers: {
                      Authorization: token ? `Bearer ${token}` : undefined,
                    },
                  }
                );
                return brand
                  ? {
                      value: brand.id,
                      label: `${brand.name}${brand.isTopBrand ? " ★" : ""}`,
                    }
                  : null;
              } catch {
                return null;
              }
            })
          );
          setSelectedBrands(
            resolved.filter(Boolean) as { value: string; label: string }[]
          );
        } catch {
          // brands not found — clear
        }
      })();
    }
  }, []);


  // Build query
  let queryParams = `page=${page}&limit=${limit}`;
  if (brandId) queryParams += `&brandId=${encodeURIComponent(brandId)}`;
  if (topBrands) queryParams += `&topBrands=true`;
  if (countryCode)
    queryParams += `&countryCode=${encodeURIComponent(countryCode)}`;
  if (publishedStatus) queryParams += `&publishedStatus=${publishedStatus}`;
  if (source) queryParams += `&source=${encodeURIComponent(source)}`;
  if (domain) queryParams += `&domain=${encodeURIComponent(domain)}`;
  if (domainMode && domainMode !== "both")
    queryParams += `&domainSearchMode=${domainMode}`;
  if (validatedOnly) queryParams += `&validatedCampaignDomainsOnly=true`;
  if (active) queryParams += `&active=true`;

  const results = useGet<IKindredOffersResponse>(
    `kindred/offers?${queryParams}`,
    false
  );

  const handleRefresh = useCallback(async () => {
    rememberSearch(page);
    await results.refetch();
  }, [results, rememberSearch, page]);

  // Re-run the last search on mount so returning from a detail page
  // restores the results, not just the filters.
  const { refetch } = results;
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !hasPreviousSearch) return;
    restoredRef.current = true;
    refetch().catch(() => undefined);
  }, [hasPreviousSearch, refetch]);

  // Only a real page change re-runs the search — the mount render and the
  // reset done by Clear Filters already match what was fetched.
  const lastFetchedPageRef = useRef(initialPage);
  useEffect(() => {
    if (lastFetchedPageRef.current === page) return;
    lastFetchedPageRef.current = page;
    handleRefresh();
  }, [page]);

  const handleClearFilters = () => {
    setBrandId("");
    setSelectedBrands([]);
    setTopBrands(false);
    setCountryCode("");
    setPublishedStatus("");
    setSource("");
    setDomain("");
    setDomainMode("both");
    setValidatedOnly(false);
    setActive(false);
    forgetSearch();
    lastFetchedPageRef.current = 1;
    setPage(1);
  };

  const pagination = results.data?.pagination;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    try {
      return format(new Date(dateStr), "yyyy-MM-dd");
    } catch {
      return dateStr;
    }
  };

  return (
    <div>
      <h4 className="mb-3">Kindred Live — Offers</h4>

      {/* Filters */}
      <Row className="mb-3 g-2">
        <Col md={3}>
          <Form.Label className="small">Brand</Form.Label>
          <BrandMultiSelect
            value={selectedBrands}
            onChange={(opts) => {
              setBrandId(opts.map((o) => o.value).join(","));
              setSelectedBrands(opts);
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small">Published Status</Form.Label>
          <Form.Select
            size="sm"
            value={publishedStatus}
            onChange={(e) => setPublishedStatus(e.target.value)}
          >
            {publishedStatusOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="small">Source / Network</Form.Label>
          <Select
            isMulti
            options={(refNetworks.data || []).map((n) => ({
              value: n,
              label: n,
            }))}
            value={
              source
                ? source.split(",").map((s: string) => ({ value: s, label: s }))
                : []
            }
            onChange={(opts) =>
              setSource((opts || []).map((o) => o.value).join(","))
            }
            placeholder="All networks..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 31,
                fontSize: "0.875rem",
              }),
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small">Country</Form.Label>
          <Select
            isMulti
            options={(refCountries.data || []).map((c) => ({
              value: c,
              label: c,
            }))}
            value={
              countryCode
                ? countryCode
                    .split(",")
                    .map((c: string) => ({ value: c, label: c }))
                : []
            }
            onChange={(opts) =>
              setCountryCode((opts || []).map((o) => o.value).join(","))
            }
            placeholder="All countries..."
            styles={{
              control: (base) => ({
                ...base,
                minHeight: 31,
                fontSize: "0.875rem",
              }),
            }}
          />
        </Col>
      </Row>

      <Row className="mb-3 g-2">
        <Col md={3}>
          <Form.Label className="small">Domain</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Search domains..."
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label className="small">Domain Source</Form.Label>
          <Form.Select
            size="sm"
            value={domainMode}
            onChange={(e) => setDomainMode(e.target.value)}
          >
            {domainModeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md="auto">
          <Form.Label className="small d-block">&nbsp;</Form.Label>
          <Form.Check
            inline
            type="checkbox"
            label="Top Brands"
            checked={topBrands}
            onChange={(e) => setTopBrands(e.target.checked)}
          />
        </Col>
        <Col md="auto">
          <Form.Label className="small d-block">&nbsp;</Form.Label>
          <Form.Check
            inline
            type="checkbox"
            label="Validated Campaign Domains Only"
            checked={validatedOnly}
            onChange={(e) => setValidatedOnly(e.target.checked)}
          />
        </Col>
        <Col md="auto">
          <Form.Label className="small d-block">&nbsp;</Form.Label>
          <Form.Check
            inline
            type="checkbox"
            label="Active Only"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Button size="sm" variant="primary" onClick={handleRefresh}>
            {results.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Search"
            )}
          </Button>{" "}
          <Button
            size="sm"
            variant="outline-primary"
            onClick={async () => {
              await handleRefresh();
            }}
          >
            Force Refresh
          </Button>{" "}
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
          <Link to="/kindred/offers/new" className="ms-2">
            <Button size="sm" variant="success">
              <Plus size={14} className="me-1" /> Create Offer
            </Button>
          </Link>
        </Col>
      </Row>

      {/* Results */}
      {results.error && (
        <div className="text-danger mb-3">Error: {results.error.message}</div>
      )}

      <div className="list-group list-group-flush">
        {results.loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" /> Loading...
          </div>
        )}
        {!results.loading && results.data?.results.length === 0 && (
          <div className="text-center text-muted py-3">
            No results. Apply filters and click Search.
          </div>
        )}
        {(results.data?.results || []).map((offer) => (
          <div
            key={offer.id}
            className={`list-group-item px-3 py-2${offer.deactivatedAt ? " bg-danger bg-opacity-10 border-danger border-opacity-25" : ""}`}
            style={{ fontSize: "0.85rem" }}
          >
            {/* Row 1: primary info */}
            <div className="d-flex align-items-center flex-wrap gap-2">
              <Link
                to={`/kindred/offers/${offer.id}`}
                className="fw-semibold text-nowrap"
              >
                {offer.summary
                  ? offer.summary.length > 60
                    ? `${offer.summary.substring(0, 60)}…`
                    : offer.summary
                  : "—"}
              </Link>
              <span className="text-muted">|</span>
              <span title={`Brand ID: ${offer.brandId}`}>
                {offer.brandName}
                {offer.isTopBrand && (
                  <Badge
                    bg="info"
                    className="ms-1"
                    style={{ fontSize: "0.65rem" }}
                  >
                    ★
                  </Badge>
                )}
              </span>
              <CopyButton text={offer.brandId} title="Copy Brand ID" />
              <span className="text-muted">|</span>
              {statusBadge(offer.publishedStatus)}
              {offer.deactivatedAt && (
                <Badge bg="dark" style={{ fontSize: "0.65rem" }}>
                  Deactivated
                </Badge>
              )}
              <span className="ms-auto d-flex align-items-center gap-2 text-nowrap">
                <small className="text-muted">
                  P{offer.priority} · {offer.source}
                </small>
                <small className="text-muted">
                  {formatDate(offer.validFrom)} → {formatDate(offer.validTo)}
                </small>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Copy JSON</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    style={{ lineHeight: 1 }}
                    onClick={() =>
                      navigator.clipboard.writeText(
                        JSON.stringify(offer, null, 2)
                      )
                    }
                  >
                    <Code size={13} />
                  </button>
                </OverlayTrigger>
              </span>
            </div>
            {/* Row 2: countries */}
            <div
              className="d-flex flex-wrap gap-3 mt-1"
              style={{ fontSize: "0.78rem" }}
            >
              <span>
                <span className="text-muted fw-semibold">Countries: </span>
                {offer.countries.length > 0 ? (
                  <span title={offer.countries.join(", ")}>
                    {offer.countries.length > 10
                      ? `${offer.countries.slice(0, 10).join(", ")}… (${offer.countries.length})`
                      : offer.countries.join(", ")}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </span>
            </div>
            {/* Row 3: domains */}
            <div
              className="d-flex flex-wrap gap-3 mt-1"
              style={{ fontSize: "0.78rem" }}
            >
              <span>
                <span className="text-muted fw-semibold">Offer Domains: </span>
                {offer.offerDomains.length > 0 ? (
                  <span title={offer.offerDomains.join(", ")}>
                    {offer.offerDomains.length > 5
                      ? `${offer.offerDomains.slice(0, 5).join(", ")}… (${offer.offerDomains.length})`
                      : offer.offerDomains.join(", ")}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </span>
              <span>
                <span className="text-muted fw-semibold">
                  Campaign Domains:{" "}
                </span>
                {offer.campaignDomains.length > 0 ? (
                  <span title={offer.campaignDomains.join(", ")}>
                    {offer.campaignDomains.length > 5
                      ? `${offer.campaignDomains.slice(0, 5).join(", ")}… (${offer.campaignDomains.length})`
                      : offer.campaignDomains.join(", ")}
                  </span>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="small text-muted">
            Showing page {pagination.page} of {pagination.totalPages} (
            {pagination.total} total)
          </span>
          <div>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() => setPage(1)}
            >
              First
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>{" "}
            <Form.Select
              size="sm"
              className="d-inline-block w-auto"
              value={page}
              onChange={(e) => setPage(Number(e.target.value))}
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </Form.Select>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === pagination.totalPages}
              onClick={() =>
                setPage((p) => Math.min(pagination.totalPages, p + 1))
              }
            >
              Next
            </Button>{" "}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page === pagination.totalPages}
              onClick={() => setPage(pagination.totalPages)}
            >
              Last
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
